import React, { useState } from 'react';
import * as workoutService from  '../../services/workoutService';

// A workout is considered "the same" if title + scheduled date match.
// Change this if your app identifies duplicates differently.
const workoutKey = (w) => {
  if (!w) return null;
  const title = (w.title || '').trim().toLowerCase();
  const date = w.scheduledDate ? String(w.scheduledDate).slice(0, 10) : '';
  return title ? `${title}|${date}` : null;
};

const getId = (w) => w && (w.id ?? w._id);

// Handles services that return either the object or an axios-style { data }
const unwrap = (res) => (res && res.data !== undefined ? res.data : res);

const WorkoutImport = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { createdCount, updatedCount, total, failures: [{ index, title, message }] }
  const [parseError, setParseError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setParseError(null);
    setResult(null);

    let parsed;
    try {
      parsed = JSON.parse(jsonInput);
    } catch (err) {
      setParseError(`Invalid JSON: ${err.message}`);
      return;
    }

    const workouts = Array.isArray(parsed) ? parsed : [parsed];
    if (workouts.length === 0) {
      setParseError('No workouts found in the pasted JSON.');
      return;
    }

    setSubmitting(true);
    let createdCount = 0;
    let updatedCount = 0;
    const failures = [];

    // Load existing workouts once and index them for quick lookup
    const existingByKey = new Map();
    try {
      const existing = unwrap(await workoutService.getAll());
      (Array.isArray(existing) ? existing : []).forEach((w) => {
        const key = workoutKey(w);
        if (key) existingByKey.set(key, w);
      });
    } catch (err) {
      setSubmitting(false);
      setParseError(
        `Could not load existing workouts: ${err.response?.data?.message || err.message}`
      );
      return;
    }

    for (let i = 0; i < workouts.length; i++) {
      const workout = workouts[i];
      try {
        const key = workoutKey(workout);
        const match = key ? existingByKey.get(key) : null;

        if (match) {
          await workoutService.update(getId(match), workout);
          existingByKey.set(key, { ...match, ...workout });
          updatedCount += 1;
        } else {
          const created = unwrap(await workoutService.create(workout));
          // Track it so a duplicate later in the same import updates instead of creating twice
          if (key) existingByKey.set(key, { ...workout, ...(created || {}) });
          createdCount += 1;
        }
      } catch (err) {
        failures.push({
          index: i,
          title: workout && workout.title ? workout.title : `Item ${i + 1}`,
          message: err.response?.data?.message || err.message || 'Unknown error'
        });
      }
    }

    setSubmitting(false);
    setResult({ createdCount, updatedCount, total: workouts.length, failures });
  };

  const handleClear = () => {
    setJsonInput('');
    setResult(null);
    setParseError(null);
  };

  const styles = {
    wrapper: { maxWidth: 600, display: 'flex', flexDirection: 'column', gap: '0.75rem' },
    label: { fontWeight: 600, fontSize: '0.9rem', color: '#111827' },
    textarea: {
      width: '100%',
      minHeight: 260,
      fontFamily: 'monospace',
      fontSize: '0.8rem',
      padding: '0.75rem',
      borderRadius: 8,
      border: '1px solid #c9d2dc',
      resize: 'vertical',
      boxSizing: 'border-box'
    },
    buttonRow: { display: 'flex', gap: '0.5rem' },
    submitButton: {
      padding: '0.5rem 1.25rem',
      borderRadius: 8,
      border: 'none',
      background: '#111827',
      color: '#ffffff',
      fontWeight: 600,
      cursor: submitting ? 'not-allowed' : 'pointer',
      opacity: submitting ? 0.6 : 1
    },
    clearButton: {
      padding: '0.5rem 1.25rem',
      borderRadius: 8,
      border: '1px solid #c9d2dc',
      background: '#ffffff',
      color: '#374151',
      fontWeight: 600,
      cursor: 'pointer'
    },
    error: { color: '#b91c1c', fontSize: '0.85rem' },
    success: { color: '#15803d', fontSize: '0.85rem' },
    failureList: { fontSize: '0.8rem', color: '#b91c1c', paddingLeft: '1.25rem', margin: 0 }
  };

  return (
    <div style={styles.wrapper}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <label style={styles.label} htmlFor="workout-json-input">
          Paste workout JSON (single object or array of objects). Existing workouts with the same title and date are updated.
        </label>
        <textarea
          id="workout-json-input"
          style={styles.textarea}
          value={jsonInput}
          onChange={(e) => setJsonInput(e.target.value)}
          placeholder='[
  {
    "title": "Week 2 - Easy Run",
    "type": "running",
    "scheduledDate": "2026-09-22",
    "tasks": [
      { "name": "Easy run", "type": "run", "targetDistance": 5 }
    ]
  }
]'
        />

        <div style={styles.buttonRow}>
          <button type="submit" style={styles.submitButton} disabled={submitting || !jsonInput.trim()}>
            {submitting ? 'Importing…' : 'Import Workouts'}
          </button>
          <button type="button" style={styles.clearButton} onClick={handleClear} disabled={submitting}>
            Clear
          </button>
        </div>
      </form>

      {parseError && <div style={styles.error}>{parseError}</div>}

      {result && (
        <div>
          <div style={result.failures.length === 0 ? styles.success : styles.error}>
            Processed {result.createdCount + result.updatedCount} of {result.total} workout
            {result.total === 1 ? '' : 's'}: {result.createdCount} created, {result.updatedCount} updated.
          </div>
          {result.failures.length > 0 && (
            <ul style={styles.failureList}>
              {result.failures.map((f) => (
                <li key={f.index}>
                  {f.title}: {f.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkoutImport;
